/**
 * The sync transport. FR-501, FR-506, FR-507.
 *
 * ## What it does
 *
 * Outbound: seal an entry under the sync key, name it with an HMAC soul, write it.
 * Inbound: open what arrives, merge it against the local copy, store the result.
 * Throughout: report a status that is true.
 *
 * ## Why the transport is injected
 *
 * `SyncTransport` is an interface rather than a direct Gun dependency. Boundary
 * rules permit this module to import the Gun layer, so that is not the reason —
 * the reason is that a two-device convergence test is otherwise a two-phone
 * manual dance, and the failures that matter here only appear with two devices.
 * With a fake graph, "device A and device B reach the same answer" is a unit test.
 *
 * ## The status is the hard part
 *
 * FR-507: never claim "synced" for a write nobody acknowledged. Gun calls back
 * optimistically, and the local IndexedDB adapter acknowledges writes itself —
 * without which nothing would ever ack while offline. So a naive implementation
 * reports success on the local write and shows "Up to date" while nothing has left
 * the device.
 *
 * That is the worst failure this app can have. Someone who believes a second
 * device has their history will act on that belief — wiping this one, replacing a
 * phone, crossing a border. The layer's `confirmedByPeer` is the only signal that
 * distinguishes "durable here" from "a relay has it", and this module treats
 * anything less as pending.
 */
import type { DayEntry, ISODate } from '@domain/types'
import { mergeEntries } from '@domain/merge'
import { open as openSealed, seal } from './crypto'
import { getEntry, putMergedEntry } from './entryStore'
import { soulForDate } from './syncNamespace'

const DEBUG_SYNC = String(import.meta.env?.VITE_SYNC_DEBUG ?? '') === '1'

function log(...args: any[]) {
  if (DEBUG_SYNC) {
    console.log(...args)
  }
}

function logError(...args: any[]) {
  if (DEBUG_SYNC) {
    console.error(...args)
  }
}

/**
 * What the engine needs from a graph, and nothing more.
 *
 * Deliberately small: three methods, no Gun types. Anything that can store a
 * string by key, notify on change, and say whether a peer is reachable can drive
 * this — which is what makes the convergence tests possible.
 */
export interface SyncTransport {
  /**
   * Store a sealed envelope under a soul.
   *
   * `confirmedByPeer` must be true ONLY when a relay acknowledged it. A local
   * store's acknowledgement is not a peer's, and reporting it as one is the
   * FR-507 failure this whole module exists to avoid.
   */
  write(soul: string, envelope: string): Promise<{ ok: boolean; confirmedByPeer: boolean }>
  /** Watch one soul. Returns an unsubscribe. */
  subscribe(soul: string, onValue: (envelope: string) => void): () => void
  /** Whether any relay is currently reachable. */
  isPeerConnected(): boolean
}

export type SyncStatus =
  /** Sync is not configured, or consent does not permit it. */
  | { kind: 'off'; reason?: string }
  /** Writes are queued and no relay is reachable. Entries are safe locally. */
  | { kind: 'offline'; pending: number }
  /** Written, waiting for a relay to confirm. NOT "up to date". */
  | { kind: 'pending'; pending: number }
  /** Every write has been confirmed by a relay. The only state that may say so. */
  | { kind: 'idle' }

export interface SyncEngineOptions {
  transport: SyncTransport
  /** Names souls. Derived by `deriveSoulKey` from the sync key material. */
  soulKey: CryptoKey
  /** Seals records for the wire. Derived by `deriveSyncSealingKey`. */
  sealingKey: CryptoKey
  /** Injected so merge decisions stay testable and this module stays pure about time. */
  now?: () => number
}

export interface SyncEngine {
  /** Seal and send one day. Safe to call on every edit. */
  push(entry: DayEntry): Promise<void>
  /** Watch a set of days for remote changes. Returns an unsubscribe for all of them. */
  watch(dates: readonly ISODate[]): Promise<() => void>
  status(): SyncStatus
  onStatusChange(listener: (status: SyncStatus, event?: { merged: boolean }) => void): () => void
  /** Retry everything still unconfirmed. Called when a relay reappears. */
  flush(): Promise<void>
  /**
   * Stop sending and reporting. Not reversible — build a new engine to resume.
   *
   * Needed because consent can be withdrawn mid-session, and "sync is off" has to
   * mean no further writes leave the device, not merely that the UI stopped
   * showing a status.
   */
  stop(): void
}

export function createSyncEngine(options: SyncEngineOptions): SyncEngine {
  const { transport, soulKey, sealingKey } = options
  const now = options.now ?? Date.now

  /*
   * Unconfirmed writes, keyed by date so repeated edits to the same day collapse
   * into one pending item rather than queueing three.
   *
   * Holding the ENTRY rather than the sealed envelope is deliberate: a retry
   * should send what the day currently says, not what it said when the first
   * attempt failed. Otherwise a flush after ten minutes offline re-sends stale
   * values and, worse, does so with timestamps that let them win a merge.
   */
  const unconfirmed = new Map<ISODate, DayEntry>()

  // Queue to limit concurrency of outbound writes
  type QueueItem = {
    entry: DayEntry
    resolve: () => void
    reject: (err: any) => void
  }
  const sendQueue: QueueItem[] = []
  let activeSends = 0
  const MAX_CONCURRENT_SENDS = 4

  const listeners = new Set<(status: SyncStatus, event?: { merged: boolean }) => void>()
  let stopped = false

  function currentStatus(): SyncStatus {
    if (stopped) return { kind: 'off' }
    if (unconfirmed.size === 0) return { kind: 'idle' }
    return transport.isPeerConnected()
      ? { kind: 'pending', pending: unconfirmed.size }
      : { kind: 'offline', pending: unconfirmed.size }
  }

  function announce(event?: { merged: boolean }): void {
    const status = currentStatus()
    for (const listener of listeners) listener(status, event)
  }

  async function processSendQueue() {
    if (stopped || activeSends >= MAX_CONCURRENT_SENDS || sendQueue.length === 0) {
      return
    }

    const { entry, resolve, reject } = sendQueue.shift()!
    activeSends++

    try {
      await send(entry)
      resolve()
    } catch (err) {
      reject(err)
    } finally {
      activeSends--
      announce()
      void processSendQueue()
    }
  }

  async function send(entry: DayEntry): Promise<void> {
    log("[syncEngine] send starting for date:", entry.date)
    const soul = await soulForDate(entry.date, soulKey)
    const envelope = JSON.stringify(await seal(entry, sealingKey))

    const result = await transport.write(soul, envelope)
    log("[syncEngine] send completed. date:", entry.date, "result:", result)
    if (result.ok && result.confirmedByPeer) unconfirmed.delete(entry.date)
    else unconfirmed.set(entry.date, entry)
  }

  async function receive(envelope: string): Promise<void> {
    log("[syncEngine] receive starting with envelope length:", envelope.length)
    let remote: DayEntry
    try {
      remote = await openSealed<DayEntry>(JSON.parse(envelope) as never, sealingKey)
      log("[syncEngine] decrypted remote entry successfully. date:", remote.date)
    } catch (e) {
      logError("[syncEngine] decrypt failed for remote entry:", e)
      return
    }

    // A malformed record that decrypts is still not something to merge blindly:
    // mergeEntries throws on mismatched dates, and a missing date would throw
    // deeper inside the store.
    if (typeof remote?.date !== 'string' || remote.meta === undefined) return

    /*
     * Storing can fail, and the realistic way is that the app locked between the
     * record arriving and this line running: `putMergedEntry` refuses to write
     * without the data key, exactly as it should.
     *
     * Caught rather than left to reject. `watch` invokes this as `void receive(…)`,
     * so a rejection here is an unhandled promise rejection — an uncaught error in
     * the browser console for a condition that is entirely normal, and one that
     * surfaced as a non-zero exit code from a test run in which every assertion
     * passed.
     *
     * Dropping the record is the right outcome: it stays on the relay, and the
     * next `watch` after unlock delivers it again. Nothing is lost, and a locked
     * device has no business decrypting anything anyway.
     */
    const local = await getEntry(remote.date)

    if (local === undefined) {
      // Nothing local to merge against. The remote record is the whole truth for
      // that day, and it keeps its own metadata — see putMergedEntry on why
      // restamping here would break convergence on a third device.
      await putMergedEntry(remote)
    } else {
      const merged = mergeEntries(local, remote, { now: now() })
      await putMergedEntry(merged.entry)
    }

    announce({ merged: true })
  }

  /** `receive`, with the failure contained. See the note inside it. */
  async function receiveSafely(envelope: string): Promise<void> {
    try {
      await receive(envelope)
    } catch {
      // Locked, or storage refused. Both leave the record on the relay for the
      // next watch, which is the recoverable outcome.
    }
  }

  return {
    push(entry) {
      if (stopped) return Promise.resolve()
      log("[syncEngine] push called for date:", entry.date)
      // Recorded as unconfirmed BEFORE the attempt, so a write that never
      // resolves — a socket that hangs rather than fails — still shows as
      // pending rather than silently disappearing from the count.
      unconfirmed.set(entry.date, entry)
      announce()

      const existing = sendQueue.find((item) => item.entry.date === entry.date)
      let promise: Promise<void>

      if (existing !== undefined) {
        existing.entry = entry
        promise = new Promise<void>((resolve, reject) => {
          const prevResolve = existing.resolve
          const prevReject = existing.reject
          existing.resolve = () => {
            prevResolve()
            resolve()
          }
          existing.reject = (err) => {
            prevReject(err)
            reject(err)
          }
        })
      } else {
        let resolveFn!: () => void
        let rejectFn!: (err: any) => void
        promise = new Promise<void>((resolve, reject) => {
          resolveFn = resolve
          rejectFn = reject
        })
        sendQueue.push({ entry, resolve: resolveFn, reject: rejectFn })
      }

      void processSendQueue()
      return promise
    },

    async watch(dates) {
      const unsubscribes = await Promise.all(
        dates.map(async (date) => {
          const soul = await soulForDate(date, soulKey)
          return transport.subscribe(soul, (envelope) => {
            void receiveSafely(envelope)
          })
        }),
      )
      return () => {
        for (const unsubscribe of unsubscribes) unsubscribe()
      }
    },

    status: currentStatus,

    onStatusChange(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    stop() {
      stopped = true
      // The queue is dropped rather than kept for a future engine. Those entries
      // are already durable locally; retaining them would mean a withdrawal of
      // consent leaves data staged for transmission.
      unconfirmed.clear()
      const error = new Error('Sync engine stopped')
      while (sendQueue.length > 0) {
        sendQueue.shift()!.reject(error)
      }
      announce()
    },

    async flush() {
      if (stopped) return
      const promises: Promise<void>[] = []
      // Snapshotted because `send` mutates the map as it succeeds, and iterating a
      // Map while deleting from it skips entries.
      for (const entry of unconfirmed.values()) {
        if (!sendQueue.some((item) => item.entry.date === entry.date)) {
          let resolveFn!: () => void
          let rejectFn!: (err: any) => void
          const promise = new Promise<void>((resolve, reject) => {
            resolveFn = resolve
            rejectFn = reject
          })
          sendQueue.push({ entry, resolve: resolveFn, reject: rejectFn })
          promises.push(promise)
        }
      }
      announce()
      void processSendQueue()
      await Promise.all(promises).catch(() => undefined)
    },
  }
}
